// TanStack Query definitions over the generated client. Pages call
// `useQuery(orgsQuery())`; keys are shared so invalidation has one spelling.
//
// The API nests resources under their parent (orgs → environments and workflows,
// environments → workspaces, workflows → runs); the "all X" lists the console shows fan
// out from the organisation list in one query function.
import { queryOptions } from "@tanstack/react-query";

import { messages } from "../i18n";
import { api, type Environment, type Organisation, type Run, type Workflow, type Workspace } from "./client";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(describe(status, body));
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function describe(status: number, body: unknown): string {
  if (body && typeof body === "object" && "error" in body && typeof body.error === "string") {
    return body.error;
  }
  const t = messages().conversation.errors;
  return status === 0 ? t.apiUnreachable : t.apiStatus(status);
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** Turn openapi-fetch's `{ data, error }` into data-or-throw, which is what Query wants. */
export async function unwrap<T>(result: Promise<FetchResult<T>>): Promise<T> {
  let r: FetchResult<T>;
  try {
    r = await result;
  } catch (e) {
    throw new ApiError(0, e instanceof Error ? e.message : e);
  }
  if (r.error !== undefined || r.data === undefined) throw new ApiError(r.response.status, r.error);
  return r.data;
}

const path = <T extends Record<string, string>>(params: T) => ({ params: { path: params } });

export const healthQuery = () =>
  queryOptions({
    queryKey: ["health"],
    queryFn: () => unwrap(api.GET("/health")),
    refetchInterval: 4_000,
    retry: false,
  });

export const orgsQuery = () =>
  queryOptions({ queryKey: ["orgs"], queryFn: () => unwrap(api.GET("/api/orgs")) });

async function fanOut<P, C>(parents: P[], children: (p: P) => Promise<C[]>): Promise<C[]> {
  return (await Promise.all(parents.map(children))).flat();
}

export const listOrgs = (): Promise<Organisation[]> => unwrap(api.GET("/api/orgs"));

export const listEnvironments = (orgs: Organisation[]): Promise<Environment[]> =>
  fanOut(orgs, (o) => unwrap(api.GET("/api/orgs/{org_id}/environments", path({ org_id: o.id }))));

export const listWorkflows = (orgs: Organisation[]): Promise<Workflow[]> =>
  fanOut(orgs, (o) => unwrap(api.GET("/api/orgs/{org_id}/workflows", path({ org_id: o.id }))));

export const listWorkspaces = (envs: Environment[]): Promise<Workspace[]> =>
  fanOut(envs, (e) => unwrap(api.GET("/api/environments/{env_id}/workspaces", path({ env_id: e.id }))));

// `limit` is a query parameter on the server, but ocs-api's `ListRunsQuery` (runs.rs) lacks
// `#[into_params(parameter_in = Query)]`, so the schema declares it in the path. There is no
// `{limit}` placeholder in the URL, so openapi-fetch drops it and the server default applies.
export const listRuns = (workflows: Workflow[]): Promise<Run[]> =>
  fanOut(workflows, (w) =>
    unwrap(api.GET("/api/workflows/{workflow_id}/runs", { params: { path: { workflow_id: w.id, limit: null } } })),
  );

export const environmentsQuery = () =>
  queryOptions({ queryKey: ["environments"], queryFn: async () => listEnvironments(await listOrgs()) });

export const workspacesQuery = () =>
  queryOptions({
    queryKey: ["workspaces"],
    queryFn: async () => listWorkspaces(await listEnvironments(await listOrgs())),
  });

export const workflowsQuery = () =>
  queryOptions({ queryKey: ["workflows"], queryFn: async () => listWorkflows(await listOrgs()) });

export const runsQuery = () =>
  queryOptions({ queryKey: ["runs"], queryFn: async () => listRuns(await listWorkflows(await listOrgs())) });

export const runQuery = (runId: string) =>
  queryOptions({
    queryKey: ["runs", runId],
    queryFn: () => unwrap(api.GET("/api/runs/{run_id}", path({ run_id: runId }))),
  });

export const runQuestionsQuery = (runId: string) =>
  queryOptions({
    queryKey: ["runs", runId, "questions"],
    queryFn: () => unwrap(api.GET("/api/runs/{run_id}/questions", path({ run_id: runId }))),
  });

export const conversationsQuery = (enabled: boolean) =>
  queryOptions({
    queryKey: ["conversations"],
    queryFn: () => unwrap(api.GET("/api/conversations")),
    enabled,
  });

export const conversationQuery = (conversationId: string, enabled: boolean) =>
  queryOptions({
    queryKey: ["conversations", conversationId],
    queryFn: () => unwrap(api.GET("/api/conversations/{conversation_id}", path({ conversation_id: conversationId }))),
    enabled,
  });

export const devicesQuery = () =>
  queryOptions({ queryKey: ["devices"], queryFn: () => unwrap(api.GET("/api/devices")) });

export const answerQuestion = (questionId: string, deviceId: string): Promise<Run> =>
  unwrap(
    api.POST("/api/questions/{question_id}/answer", {
      ...path({ question_id: questionId }),
      body: { device_id: deviceId },
    }),
  );
